import { useState, useEffect } from 'react';
import logger from '@/utils/logger';

interface EditableDescriptionProps {
  value: string | null;
  onChange: (newValue: string | null) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function EditableDescription({
  value,
  onChange,
  disabled = false,
  placeholder = 'Click to add description',
  className = '',
}: EditableDescriptionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value || '');
  const [isLoading, setIsLoading] = useState(false);

  // Sync editedValue with value prop when it changes
  useEffect(() => {
    setEditedValue(value || '');
  }, [value]);

  const handleSave = async () => {
    const trimmedValue = editedValue.trim();

    // Don't save if value hasn't changed
    if (trimmedValue === (value || '')) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      // Allow empty description (null)
      await onChange(trimmedValue || null);
      setIsEditing(false);
      logger.info('EditableDescription', 'Description updated successfully');
    } catch (error) {
      logger.error('EditableDescription', 'Failed to update description:', error);
      setEditedValue(value || ''); // Rollback
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedValue(value || '');
    setIsEditing(false);
    logger.debug('EditableDescription', 'Edit cancelled');
  };

  return (
    <div className={className}>
      {disabled || !isEditing ? (
        <p
          onClick={() => !disabled && setIsEditing(true)}
          className="cursor-pointer hover:bg-muted/20 px-2 rounded transition-colors text-muted"
          title={disabled ? undefined : 'Click to edit'}
        >
          {value || placeholder}
        </p>
      ) : (
        <input
          type="text"
          value={editedValue}
          onChange={(e) => setEditedValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            }
          }}
          autoFocus
          disabled={isLoading}
          className="border border-border bg-surface text-muted px-2 py-1 rounded w-full focus:outline-none focus:ring-2 focus:ring-primary"
          maxLength={500}
        />
      )}
    </div>
  );
}
